import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const getRendimientoInfo = (rendimiento) => {
  if (rendimiento == null) return null;
  const r = parseFloat(rendimiento);
  if (r > 50) return { label: 'Excelente', color: '#3A7F6B', bg: 'rgba(58,127,107,0.18)', border: 'rgba(58,127,107,0.40)', emoji: '🟢' };
  if (r >= 45) return { label: 'Bueno', color: '#5aab8e', bg: 'rgba(58,127,107,0.12)', border: 'rgba(58,127,107,0.25)', emoji: '🟢' };
  if (r >= 40) return { label: 'Revisar', color: '#C9A24D', bg: 'rgba(201,162,77,0.18)', border: 'rgba(201,162,77,0.40)', emoji: '🟡' };
  return { label: 'Bajo Rendimiento', color: '#E05252', bg: 'rgba(224,82,82,0.18)', border: 'rgba(224,82,82,0.40)', emoji: '🔴' };
};

const InfoRow = ({ label, value, valueColor = undefined, badge = undefined }) => (
  <div className="flex items-start justify-between py-2.5" style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
    <span className="text-sm font-oranienbaum text-white/45 flex-shrink-0 mr-4">{label}</span>
    {badge ? (
      <span
        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-bold font-oranienbaum"
        style={{ background: badge?.bg, border: `1px solid ${badge?.border}`, color: badge?.color }}
      >
        {badge?.emoji} {badge?.label}
      </span>
    ) : (
      <span
        className="text-sm font-oranienbaum font-medium text-right"
        style={{ color: valueColor || 'rgba(255,255,255,0.85)' }}
      >
        {value || 'Sin registrar'}
      </span>
    )}
  </div>
);

const SectionTitle = ({ icon, label }) => (
  <div className="flex items-center gap-2 mb-3 mt-1">
    <div className="w-1 h-5 rounded-full" style={{ background: '#D6EF4E' }} />
    <Icon name={icon} size={14} style={{ color: '#D6EF4E' }} />
    <h3 className="text-sm font-bold font-oranienbaum uppercase tracking-wider" style={{ color: '#D6EF4E' }}>
      {label}
    </h3>
  </div>
);

const LoncheadosDetailModal = ({ loncheado, clientes, empleados, productos, onClose, onEdit }) => {
  const [lightboxUrl, setLightboxUrl] = useState(null);

  if (!loncheado) return null;

  const getClienteName = (id) => clientes?.find(c => c?.id === id)?.nombre || 'Sin registrar';
  const getEmpleadoName = (id) => empleados?.find(e => e?.id === id)?.nombre || 'Sin registrar';
  const getProductoName = (id) => productos?.find(p => p?.id === id)?.nombre || 'Sin registrar';

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Sin registrar';
    return new Date(dateStr)?.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return 'Sin registrar';
    return new Date(dateStr)?.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
  };

  const createdAt = loncheado?.createdAt || loncheado?.created_at;
  const pesoInicial = loncheado?.pesoInicial ?? loncheado?.peso_inicial ?? loncheado?.peso ?? loncheado?.peso_loncheado;
  const pesoAprovechado = loncheado?.pesoAprovechado ?? loncheado?.peso_aprovechado;
  const merma = loncheado?.merma;
  const rendimiento = loncheado?.rendimiento;
  const tipo = loncheado?.tipo;
  const fotoUrl = loncheado?.fotoUrl || loncheado?.foto_url;
  const observaciones = loncheado?.observaciones;
  const loteInterno = loncheado?.loteInterno || loncheado?.lote_interno;
  const loteExterno = loncheado?.loteExterno || loncheado?.lote_externo;
  const emplatado = loncheado?.emplatado;
  const numeroBlisteres = loncheado?.numeroBlisteres ?? loncheado?.numero_blisteres;
  const estadoRendimiento = loncheado?.estadoRendimiento || loncheado?.estado_rendimiento;

  const rendInfo = getRendimientoInfo(rendimiento);

  // Parse photos
  let fotos = [];
  if (fotoUrl) {
    try {
      const parsed = JSON.parse(fotoUrl);
      fotos = Array.isArray(parsed) ? parsed : [fotoUrl];
    } catch {
      fotos = [fotoUrl];
    }
  }

  const tipoLabels = {
    EMPLATADO: 'Emplatado',
    NORMAL: 'Normal',
    GRANDE_Y_EMPLATADO: 'Grande y Emplatado',
    PALETILLA: 'Paletilla',
  };

  return (
    <>
      <div
        className="fixed inset-0 z-50 flex items-center justify-center p-4"
        style={{ background: 'rgba(0,0,0,0.75)' }}
        onClick={onClose}
      >
        <div
          className="w-full max-w-2xl max-h-[92vh] overflow-y-auto rounded-2xl"
          style={{ background: '#1A1A1A', border: '1px solid rgba(255,255,255,0.10)' }}
          onClick={e => e?.stopPropagation()}
        >
          {/* Header */}
          <div
            className="flex items-center justify-between px-6 py-4 sticky top-0 z-10"
            style={{ background: '#141414', borderBottom: '1px solid rgba(255,255,255,0.08)' }}
          >
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: 'rgba(214,239,78,0.12)' }}>
                <Icon name="FileText" size={18} style={{ color: '#D6EF4E' }} />
              </div>
              <div>
                <h2 className="text-lg font-bold font-oranienbaum" style={{ color: '#D6EF4E' }}>
                  Detalle del Loncheado
                </h2>
                <p className="text-xs font-oranienbaum text-white/35">
                  ID: {loncheado?.id?.substring(0, 8)}…
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {onEdit && (
                <button
                  onClick={() => { onClose?.(); onEdit?.(loncheado); }}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-oranienbaum font-medium transition-all"
                  style={{ background: 'rgba(58,127,107,0.12)', border: '1px solid rgba(58,127,107,0.30)', color: '#3A7F6B' }}
                >
                  <Icon name="Edit" size={13} />
                  Editar
                </button>
              )}
              <button
                onClick={onClose}
                className="w-8 h-8 rounded-lg flex items-center justify-center transition-colors hover:bg-white/10"
                style={{ color: 'rgba(255,255,255,0.5)' }}
              >
                <Icon name="X" size={16} />
              </button>
            </div>
          </div>

          <div className="p-6 space-y-5">
            {/* Información General */}
            <div className="rounded-xl p-4" style={{ background: '#111111', border: '1px solid rgba(255,255,255,0.06)' }}>
              <SectionTitle icon="User" label="Información General" />
              <InfoRow label="Cliente" value={getClienteName(loncheado?.clienteId || loncheado?.cliente_id)} />
              <InfoRow label="Empleado" value={getEmpleadoName(loncheado?.empleadoId || loncheado?.empleado_id)} />
              <InfoRow label="Producto" value={getProductoName(loncheado?.productoId || loncheado?.producto_id)} />
              <InfoRow label="Fecha" value={formatDate(createdAt)} />
              <InfoRow label="Hora" value={formatTime(createdAt)} />
            </div>

            {/* Datos de la Pieza */}
            <div className="rounded-xl p-4" style={{ background: '#111111', border: '1px solid rgba(255,255,255,0.06)' }}>
              <SectionTitle icon="Scale" label="Datos de la Pieza" />
              <InfoRow
                label="Tipo de Gestión"
                value={tipoLabels?.[tipo] || tipo || 'Sin registrar'}
              />
              <InfoRow
                label="Emplatado"
                value={emplatado ? 'Sí' : 'No'}
              />
              <InfoRow
                label="Peso Inicial"
                value={pesoInicial != null ? `${parseFloat(pesoInicial)?.toFixed(2)} kg` : null}
              />
              <InfoRow
                label="Peso Aprovechado"
                value={pesoAprovechado != null ? `${parseFloat(pesoAprovechado)?.toFixed(2)} kg` : null}
              />
              <InfoRow
                label="Nº de Blísteres"
                value={numeroBlisteres != null ? String(numeroBlisteres) : null}
              />
            </div>

            {/* Rendimiento y Merma */}
            <div className="rounded-xl p-4" style={{ background: '#111111', border: '1px solid rgba(255,255,255,0.06)' }}>
              <SectionTitle icon="BarChart2" label="Rendimiento y Merma" />
              <InfoRow
                label="Merma"
                value={merma != null ? `${parseFloat(merma)?.toFixed(2)} kg` : null}
                valueColor="#E05252"
              />
              <InfoRow
                label="Rendimiento"
                value={rendimiento != null ? `${parseFloat(rendimiento)?.toFixed(1)}%` : null}
                valueColor={rendInfo?.color}
              />
              <div className="flex items-start justify-between py-2.5" style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                <span className="text-sm font-oranienbaum text-white/45">Estado</span>
                {rendInfo ? (
                  <span
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-bold font-oranienbaum"
                    style={{ background: rendInfo?.bg, border: `1px solid ${rendInfo?.border}`, color: rendInfo?.color }}
                  >
                    {rendInfo?.emoji} {rendInfo?.label}
                  </span>
                ) : (
                  <span className="text-sm font-oranienbaum text-white/85">Sin registrar</span>
                )}
              </div>
              {rendimiento != null && (
                <div className="mt-3">
                  <div className="h-2 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.06)' }}>
                    <div
                      className="h-full rounded-full transition-all duration-500"
                      style={{
                        width: `${Math.min(parseFloat(rendimiento), 100)}%`,
                        background: rendInfo?.color || '#D6EF4E'
                      }}
                    />
                  </div>
                  <div className="flex justify-between mt-1">
                    <span className="text-[9px] font-oranienbaum text-white/40">0%</span>
                    <span className="text-[9px] font-oranienbaum font-bold" style={{ color: rendInfo?.color }}>
                      {parseFloat(rendimiento)?.toFixed(1)}%
                    </span>
                    <span className="text-[9px] font-oranienbaum text-white/40">100%</span>
                  </div>
                </div>
              )}
            </div>

            {/* Trazabilidad */}
            <div className="rounded-xl p-4" style={{ background: '#111111', border: '1px solid rgba(255,255,255,0.06)' }}>
              <SectionTitle icon="Tag" label="Trazabilidad" />
              <InfoRow label="Lote Interno" value={loteInterno || null} />
              <InfoRow label="Lote Externo" value={loteExterno || null} />
            </div>

            {/* Observaciones */}
            {observaciones && (
              <div className="rounded-xl p-4" style={{ background: '#111111', border: '1px solid rgba(255,255,255,0.06)' }}>
                <SectionTitle icon="MessageSquare" label="Observaciones" />
                <p className="text-sm font-oranienbaum text-white/75 whitespace-pre-wrap leading-relaxed">
                  {observaciones}
                </p>
              </div>
            )}

            {/* Fotografías */}
            <div className="rounded-xl p-4" style={{ background: '#111111', border: '1px solid rgba(255,255,255,0.06)' }}>
              <SectionTitle icon="Camera" label="Fotografías" />
              {fotos?.length > 0 ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {fotos?.map((url, i) => (
                    <div
                      key={i}
                      className="relative group rounded-lg overflow-hidden cursor-zoom-in aspect-square"
                      style={{ border: '1px solid rgba(255,255,255,0.08)' }}
                      onClick={() => setLightboxUrl(url)}
                    >
                      <img
                        src={url}
                        alt={`Fotografía ${i + 1} del loncheado`}
                        className="w-full h-full object-cover"
                        onError={e => { e.target.src = '/assets/images/no_image.png'; }}
                      />
                      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity" style={{ background: 'rgba(0,0,0,0.5)' }}>
                        <Icon name="ZoomIn" size={20} style={{ color: '#fff' }} />
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-8 gap-2">
                  <Icon name="ImageOff" size={32} style={{ color: 'rgba(255,255,255,0.15)' }} />
                  <p className="text-sm font-oranienbaum text-white/35">Sin fotografías registradas</p>
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div
            className="flex items-center justify-end gap-3 px-6 py-4"
            style={{ background: '#141414', borderTop: '1px solid rgba(255,255,255,0.08)' }}
          >
            {onEdit && (
              <button
                onClick={() => { onClose?.(); onEdit?.(loncheado); }}
                className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-oranienbaum font-medium transition-all"
                style={{ background: 'rgba(58,127,107,0.12)', border: '1px solid rgba(58,127,107,0.30)', color: '#3A7F6B' }}
              >
                <Icon name="Edit" size={14} />
                Editar Registro
              </button>
            )}
            <button
              onClick={onClose}
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-oranienbaum font-medium transition-all"
              style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.70)' }}
            >
              <Icon name="X" size={14} />
              Cerrar
            </button>
          </div>
        </div>
      </div>

      {/* Lightbox */}
      {lightboxUrl && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/90 p-4 cursor-zoom-out"
          onClick={() => setLightboxUrl(null)}
        >
          <button
            className="absolute top-4 right-4 rounded-full p-2 transition-colors"
            style={{ background: 'rgba(255,255,255,0.15)' }}
            onClick={() => setLightboxUrl(null)}
          >
            <Icon name="X" size={22} style={{ color: '#fff' }} />
          </button>
          <img
            src={lightboxUrl}
            alt="Fotografía ampliada del loncheado"
            className="max-w-full max-h-full object-contain rounded-xl"
            onClick={e => e?.stopPropagation()}
          />
        </div>
      )}
    </>
  );
};

export default LoncheadosDetailModal;
